import React, { useEffect, useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Box,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  CircularProgress,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useApi } from '../contexts/Api';

const renderTiers = (tiersRaw) => {
  let tiers = tiersRaw;
  if (typeof tiers === 'string') {
    try { tiers = JSON.parse(tiers); } catch { tiers = []; }
  }
  if (!Array.isArray(tiers) || !tiers.length) return <Typography variant="caption" color="text.secondary">—</Typography>;
  return (
    <Box display="flex" flexDirection="column" gap={0.5}>
      {tiers.map((t, i) => (
        <Chip
          key={i}
          label={`${t.min_qty}${t.max_qty ? '–' + t.max_qty : '+'} → ${t.discount_pct}%`}
          size="small"
          sx={{
            backgroundColor: t.discount_pct > 0 ? '#fef3c7' : '#f3f4f6',
            color: t.discount_pct > 0 ? '#92400e' : '#374151',
            fontWeight: 500,
            fontSize: '11px'
          }}
        />
      ))}
    </Box>
  );
};

const SortableRow = ({ rule, onEdit, onDelete, categoryMap, brandMap }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: rule.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
    backgroundColor: isDragging ? '#f9fafb' : undefined
  };

  const renderFilters = () => {
    const chips = [];

    const pushIds = (ids, kind, color) => {
      if (!Array.isArray(ids) || !ids.length) return;
      ids.forEach(id => {
        const label =
          kind === 'brand'
            ? brandMap[id] || `Brand #${id}`
            : categoryMap[id] || `Cat #${id}`;
        chips.push(
          <Chip
            key={`${kind}-${id}`}
            label={label}
            size="small"
            sx={{ fontSize: '10px', height: '20px', backgroundColor: color.bg, color: color.fg }}
          />
        );
      });
    };

    pushIds(rule.parent_ids, 'parent', { bg: '#dbeafe', fg: '#1e40af' });
    pushIds(rule.cat_ids, 'cat', { bg: '#dcfce7', fg: '#166534' });
    pushIds(rule.marka_ids, 'brand', { bg: '#ffedd5', fg: '#9a3412' });

    if (rule.min_price || rule.max_price) {
      chips.push(
        <Chip
          key="price"
          label={`€${rule.min_price || 0}–${rule.max_price || '∞'}`}
          size="small"
          variant="outlined"
          sx={{ fontSize: '10px', height: '20px' }}
        />
      );
    }
    if (rule.min_weight || rule.max_weight) {
      chips.push(
        <Chip
          key="weight"
          label={`${rule.min_weight || 0}–${rule.max_weight || '∞'}kg`}
          size="small"
          variant="outlined"
          sx={{ fontSize: '10px', height: '20px' }}
        />
      );
    }
    if (rule.sku_list) {
      const skus = rule.sku_list.split(',').filter(Boolean);
      chips.push(
        <Chip
          key="sku"
          label={`${skus.length} SKU${skus.length === 1 ? '' : 's'}`}
          size="small"
          variant="outlined"
          sx={{ fontSize: '10px', height: '20px' }}
        />
      );
    }
    if (!chips.length) return <Chip label="All products" size="small" variant="outlined" />;
    return (
      <Box display="flex" flexWrap="wrap" gap={0.5}>
        {chips}
      </Box>
    );
  };

  return (
    <TableRow ref={setNodeRef} style={style} hover>
      <TableCell sx={{ width: 50, p: 0.5 }}>
        <Box
          {...attributes}
          {...listeners}
          sx={{
            cursor: isDragging ? 'grabbing' : 'grab',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#9ca3af',
            '&:hover': { color: '#6b7280' }
          }}
        >
          <DragIndicatorIcon />
        </Box>
      </TableCell>
      <TableCell>
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {rule.name || 'Unnamed Rule'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ID: {rule.id}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>{renderFilters()}</TableCell>
      <TableCell>{renderTiers(rule.tiers)}</TableCell>
      <TableCell align="center">
        <Chip
          label={rule.active == 1 ? 'Active' : 'Inactive'}
          color={rule.active == 1 ? 'success' : 'default'}
          size="small"
        />
      </TableCell>
      <TableCell align="center">
        <IconButton size="small" onClick={() => onEdit(rule)} color="primary">
          <EditIcon />
        </IconButton>
        <IconButton size="small" onClick={() => onDelete(rule)} color="error">
          <DeleteIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

const VolumeDiscountList = ({ rules, onEdit, onRefresh }) => {
  const { categories, brands } = useApi();
  const [items, setItems] = useState(rules);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [savedSnack, setSavedSnack] = useState(false);

  useEffect(() => {
    setItems(rules);
  }, [rules]);

  const categoryMap = useMemo(() => {
    const m = {};
    (categories || []).forEach(c => { m[Number(c.id)] = c.name_en || `#${c.id}`; });
    return m;
  }, [categories]);

  const brandMap = useMemo(() => {
    const m = {};
    (brands || []).forEach(b => { m[Number(b.id)] = b.name || `#${b.id}`; });
    return m;
  }, [brands]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const persistOrder = async (orderedIds) => {
    setSavingOrder(true);
    try {
      const res = await fetch('https://elnaf.fi/apiElnaf/api/volume_discounts.php?action=reorder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: orderedIds })
      });
      const data = await res.json();
      if (!res.ok || data.result === 0) {
        alert(data.error || 'Failed to save order');
        await onRefresh();
        return;
      }
      setSavedSnack(true);
    } catch (e) {
      console.error('Reorder error:', e);
      alert('Network error while saving order');
      await onRefresh();
    } finally {
      setSavingOrder(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex(r => r.id === active.id);
    const newIndex = items.findIndex(r => r.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const next = arrayMove(items, oldIndex, newIndex);
    setItems(next);
    persistOrder(next.map(r => r.id));
  };

  const handleDeleteClick = (rule) => {
    setSelectedRule(rule);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedRule) return;
    setDeleteLoading(true);
    try {
      const response = await fetch(`https://elnaf.fi/apiElnaf/api/volume_discounts.php?id=${selectedRule.id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (response.ok && (data.result === 1 || data.success)) {
        setDeleteDialogOpen(false);
        setSelectedRule(null);
        await onRefresh();
      } else {
        alert(data.error || data.message || 'Failed to delete rule');
      }
    } catch (error) {
      console.error('Error deleting rule:', error);
      alert('Failed to delete rule');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map(r => r.id)} strategy={verticalListSortingStrategy}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 50 }}>
                    <Tooltip title="Drag to reorder — top of list has highest priority">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                        <DragIndicatorIcon fontSize="small" />
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Filters</TableCell>
                  <TableCell>Tiers</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map(rule => (
                  <SortableRow
                    key={rule.id}
                    rule={rule}
                    onEdit={onEdit}
                    onDelete={handleDeleteClick}
                    categoryMap={categoryMap}
                    brandMap={brandMap}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SortableContext>
      </DndContext>

      <Dialog open={deleteDialogOpen} onClose={() => !deleteLoading && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Volume Discount Rule</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the rule "{selectedRule?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading && <CircularProgress size={20} color="inherit" />}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={savedSnack}
        autoHideDuration={1800}
        onClose={() => setSavedSnack(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" variant="filled" sx={{ width: '100%' }}>
          Order saved
        </Alert>
      </Snackbar>

      {savingOrder && (
        <Box sx={{ position: 'fixed', bottom: 16, left: 16, display: 'flex', alignItems: 'center', gap: 1, backgroundColor: 'rgba(0,0,0,0.7)', color: 'white', px: 2, py: 1, borderRadius: 1, fontSize: 12 }}>
          <CircularProgress size={14} color="inherit" /> Saving order...
        </Box>
      )}
    </>
  );
};

export default VolumeDiscountList;
